'use client';

import { useEffect, useState } from 'react';
import { redirect, useSearchParams } from 'next/navigation';
import Script from 'next/script';
import { Variant } from 'shared/types/product';
import { Product } from 'shared/types/product/Product';
import TrustPilotSnippet from 'components/Scripts/TrustpilotSnippet';
import { RouteHelpers } from 'helpers/routerHelpers';
import { gtmViewItem } from 'helpers/utils/gtm-events/gtmEccomerce';
import { LineItemMapper } from 'helpers/utils/gtm-events/mapper/LineItemMapper';
import { DataSource } from 'types/datasource';
import { useCart, useProjectSettings, useAccount } from 'frontastic/hooks';
import { TasticProps } from 'frontastic/tastics/types';
import ProductBreadcrumbs from './components/ProductBreadcrumbs';
import ProductGroupJsonSchema from './components/ProductGroupJsonSchema';
import ProductInformationAccordion from './components/ProductInformationAccordion';
import ProductRecentlyViewedSection from './components/ProductRecentlyViewedSection';
import ProductShopCollection from './components/ProductShopCollection';
import ProductsYouMayAlsoLikeSection from './components/ProductsYouMayAlsoLike';
import ProductTile from './components/ProductTile';
import useAddToWishlistBackground from './hooks/useAddToWishlistBackground';

const ProductDetailsTastic = ({ data }: TasticProps<DataSource<{ product: Product }>>) => {
  if (!data?.data?.dataSource?.product) return null;

  const product = data.data.dataSource.product;

  return (
    <>
      <ProductGroupJsonSchema product={product} />
      <TrustPilotSnippet />
      <WishlistHandler />
      <ProductBreadcrumbs breadcrumbs={product.breadcrumbs || []} productName={product.name} />
      <ProductTileAndAccordionContainer {...product} />
      {!!product.shopCollection && (
        <ProductShopCollection
          shopCollectionProducts={product.shopCollection}
          shopCollectionUrl={product.collection as string}
          product={product}
        />
      )}
      {product.youMayAlsoLike && product.youMayAlsoLike.length > 0 && (
        <ProductsYouMayAlsoLikeSection products={product.youMayAlsoLike} product={product} />
      )}
      <ProductRecentlyViewedSection sku={product?.masterVariant?.sku.split(' ')[0] as string} />
      <ProductMyRegistryContainer />
    </>
  );
};

const WishlistHandler = () => {
  // Move here to avoid re-render and duplicate gtm triggers 'view_item'
  useAddToWishlistBackground();

  return null;
};
const ProductMyRegistryContainer = () => {
  const { data: projectData } = useProjectSettings();
  return (
    <>
      {projectData?.giftRegistry && (
        <Script src={`${projectData.giftRegistry}${new Date().getTime()}`} strategy="afterInteractive" defer />
      )}
    </>
  );
};

const ProductTileAndAccordionContainer = (product: Product) => {
  const search = useSearchParams();
  const selectedColor = RouteHelpers.slugify(
    search.get('color') || product.masterVariant?.attributes?.actualColor || '',
  );
  const selectedSize = search.get('size') || product.masterVariant?.attributes?.size;
  const selectedVariant = product.variants.find(
    (variant) =>
      RouteHelpers.slugify(variant.attributes?.actualColor || '') === selectedColor &&
      variant.attributes?.size === selectedSize,
  );

  if (!selectedVariant) return redirect(product._url ?? '/');
  return (
    <>
      {product && selectedVariant && <GtmViewItemHandler product={product} selectedVariant={selectedVariant} />}
      <ProductTile
        labelSelectedColor={selectedVariant?.attributes?.actualColor}
        selectedColor={selectedColor}
        product={product}
        selectedSize={selectedSize}
        selectedVariant={selectedVariant}
      />
      <ProductInformationAccordion product={product} selectedVariant={selectedVariant} />
      <script
        type="application/ld+json"
        id="product-schema"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: product.name,
            description: product.description,
            image: product.variants[0]?.images,
            sku: product.variants[0]?.sku?.replace(/[^a-zA-Z0-9]/g, ''),
            brand: {
              '@type': 'Brand',
              name: 'Shades of Light',
            },
            ageGroup: 'adult',
            gender: 'unisex',
            offers: {
              '@type': 'Offer',
              itemCondition: 'https://schema.org/NewCondition',
              url: `https://www.shadesoflight.com${product._url}`,
              priceCurrency: product?.variants?.[0]?.price?.currencyCode,
              price: (product?.variants?.[0]?.price?.centAmount || 0) / 100,
              availability: 'https://schema.org/InStock',
              ...(product.variants[0].discountedPrice && {
                salePrice: (product?.variants?.[0]?.originalPrice?.centAmount || 0) / 100,
              }),
            },
            ...(product?.variants?.[0].attributes?.actualColor && {
              color: product?.variants?.[0].attributes.actualColor,
            }),
            ...(product?.variants?.[0].attributes?.actualSize && {
              size: product?.variants?.[0].attributes.actualSize,
              sizeSystem: 'US',
            }),
          }),
        }}
      />
    </>
  );
};
const GtmViewItemHandler = ({ product, selectedVariant }: { product: Product; selectedVariant: Variant }) => {
  const [hasTriggerEvent, setHasTriggerEvent] = useState<boolean>(false);
  const { account, isResolved: accountIsResolved } = useAccount();
  const { data: cart, isResolved: cartIsResolved } = useCart();

  useEffect(() => {
    const checkDataLayer = () => {
      if (
        typeof window !== 'undefined' &&
        window.dataLayer &&
        !hasTriggerEvent &&
        cartIsResolved &&
        accountIsResolved
      ) {
        gtmViewItem({
          itemDataLayer: LineItemMapper.fromProductToItemDataLayer(product, 1, selectedVariant),
          cart,
          account,
        });
        setHasTriggerEvent(true);
        clearInterval(interval);
      }
    };

    const interval = setInterval(checkDataLayer, 200);

    return () => clearInterval(interval);
  }, [cart, cartIsResolved, accountIsResolved]);

  return <></>;
};
export default ProductDetailsTastic;
